<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { getColorHEX } from '../../../colors';
import { useDraftStore } from '@/dashboard/store/draftStore';
import { useDraftImageStore } from '@/dashboard/store/draftImageStore';

const props = defineProps<{
  zoom: number;
}>();

const draftStore = useDraftStore();
const draftImageStore = useDraftImageStore();

const { allowCommenting } = storeToRefs(draftStore);
const { drawingPoint, activePoint, defaultColor } = storeToRefs(draftImageStore);

const color = computed(() => getColorHEX(defaultColor.value));

const drawRef = ref<HTMLElement | null>(null);
const canvas = ref<HTMLCanvasElement | null>(null);
const drawing = ref(false);
const startPoint = ref<{ x: number; y: number } | null>(null);
const rectangle = ref<{ x: number; y: number; width: number; height: number } | null>(null);

const isSpacePressed = ref(false);

const context = computed(() => canvas.value?.getContext('2d') || null);

const calculateRect = (startPoint: { x: number; y: number }, endPoint: { x: number; y: number }) => ({
  x: Math.min(startPoint.x, endPoint.x),
  y: Math.min(startPoint.y, endPoint.y),
  width: Math.abs(endPoint.x - startPoint.x),
  height: Math.abs(endPoint.y - startPoint.y),
});

const getPoint = ({ clientX, clientY }: { clientX: number; clientY: number }) => {
  if (!drawRef.value) return { x: 0, y: 0 };
  const { x, y, width, height } = drawRef.value.getBoundingClientRect();

  return {
    x: Math.round(Math.max(0, Math.min(clientX - x, width))),
    y: Math.round(Math.max(0, Math.min(clientY - y, height))),
  };
};

const onKeyDown = (e: KeyboardEvent) => {
  if (e.code === 'Space') {
    isSpacePressed.value = true;
  }
};

const onKeyUp = (e: KeyboardEvent) => {
  if (e.code === 'Space') {
    isSpacePressed.value = false;
  }
};

const mouseDown = (event: MouseEvent) => {
  if (event.type === 'touchstart') {
    return;
  }

  if (isSpacePressed.value) {
    return;
  }

  if (event.button === 0) {
    draftImageStore.setRightMousePoint(null);

    if (drawingPoint.value === null && activePoint.value === null && allowCommenting.value) {
      drawing.value = true;
      startPoint.value = getPoint(event);
    } else {
      draftImageStore.closeTempPoint();
    }
  } else if (event.button === 2) {
    const point = getPoint(event);
    draftImageStore.closeTempPoint();
    draftImageStore.setRightMousePoint(point);
  }
};

const touchStart = (event: TouchEvent) => {
  if (isSpacePressed.value) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();

  if (event.touches.length > 0) {
    if (drawingPoint.value === null && activePoint.value === null && allowCommenting.value) {
      drawing.value = true;
      startPoint.value = getPoint(event.touches[0]);

      window.addEventListener('touchmove', toucheMove, { passive: false });
      window.addEventListener('touchend', mouseUp);
    } else {
      draftImageStore.closeTempPoint();
    }
  }
};

const mouseUp = async (event: MouseEvent | TouchEvent) => {
  const endPoint = getPoint(event.type === 'touchend' ? (event as TouchEvent).changedTouches[0] : (event as MouseEvent));

  if (drawing.value && startPoint.value) {
    let position = 1;

    if (startPoint.value.x < endPoint.x && startPoint.value.y < endPoint.y) {
      position = 2;
    } else if (startPoint.value.x > endPoint.x && startPoint.value.y > endPoint.y) {
      position = 3;
    } else if (startPoint.value.x < endPoint.x && startPoint.value.y > endPoint.y) {
      position = 4;
    }

    const rect = calculateRect(startPoint.value, endPoint);

    const data = {
      size: {
        width: Math.round(rect.width / props.zoom),
        height: Math.round(rect.height / props.zoom),
      },
      location: {
        x: Math.round(endPoint.x / props.zoom),
        y: Math.round(endPoint.y / props.zoom),
      },
      position,
    };

    draftImageStore.createTempPoint(data);
    rectangle.value = null;
  }

  drawing.value = false;
  window.removeEventListener('touchmove', toucheMove);
  window.removeEventListener('touchend', mouseUp);
};

const mouseMove = (event: MouseEvent) => {
  if (isSpacePressed.value) {
    drawing.value = false;
    rectangle.value = null;
    startPoint.value = null;
    return;
  }

  if (drawing.value === true && startPoint.value) {
    setTimeout(() => {
      const endPoint = getPoint(event);
      rectangle.value = calculateRect(startPoint.value!, endPoint);
    }, 0);
  }
};

const toucheMove = (event: TouchEvent) => {
  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();

  if (drawing.value === true && event.touches.length > 0 && startPoint.value) {
    const endPoint = getPoint(event.touches[0]);
    rectangle.value = calculateRect(startPoint.value, endPoint);
  }
};

watch(drawing, async (newValue, oldValue) => {
  if (newValue === false) {
    startPoint.value = null;
    rectangle.value = null;
  }
});

(CanvasRenderingContext2D.prototype as any).dashedLine = function (pt1: { x: number; y: number }, pt2: { x: number; y: number }, dashLen?: number) {
  if (dashLen === undefined) dashLen = 2;

  let dX = pt2.x - pt1.x;
  let dY = pt2.y - pt1.y;
  let dashes = Math.floor(Math.sqrt(dX * dX + dY * dY) / dashLen);
  let dashX = dX / dashes;
  let dashY = dY / dashes;

  let q = 0;
  while (q++ < dashes) {
    pt1.x += dashX;
    pt1.y += dashY;
    this[q % 2 === 0 ? 'moveTo' : 'lineTo'](pt1.x, pt1.y);
  }
  this[q % 2 === 0 ? 'moveTo' : 'lineTo'](pt2.x, pt2.y);
};
(CanvasRenderingContext2D.prototype as any).roundRectangle = function (
  pt1: { x: number; y: number },
  pt2: { x: number; y: number },
  pt3: { x: number; y: number },
  pt4: { x: number; y: number },
  dashed?: boolean,
  dashLen?: number
) {
  function line(ctx: CanvasRenderingContext2D, x1: number, y1: number, x2: number, y2: number, dashed?: boolean) {
    if (dashed) (ctx as any).dashedLine({ x: x1, y: y1 }, { x: x2, y: y2 });
    else {
      ctx.lineTo(x2, y2);
    }
  }

  let radius = 8;
  //this.beginPath();
  this.moveTo(pt1.x + radius, pt1.y); //Added
  line(this, pt1.x + radius, pt1.y, pt2.x - radius, pt2.y, dashed);
  this.quadraticCurveTo(pt2.x, pt2.y, pt2.x, pt2.y + radius);
  line(this, pt2.x, pt2.y + radius, pt3.x, pt3.y - radius, dashed);
  this.quadraticCurveTo(pt3.x, pt3.y, pt3.x - radius, pt3.y);
  line(this, pt3.x - radius, pt3.y, pt4.x + radius, pt4.y, dashed);
  this.quadraticCurveTo(pt4.x, pt4.y, pt4.x, pt4.y - radius);
  line(this, pt4.x, pt4.y - radius, pt1.x, pt1.y + radius, dashed);
  this.quadraticCurveTo(pt1.x, pt1.y, pt1.x + radius, pt1.y);

  //this.closePath();
  this.stroke();
  //this.fill();
};

onMounted(() => {
  window.addEventListener('mousemove', mouseMove);
  window.addEventListener('mouseup', mouseUp);

  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup', onKeyUp);

  drawRef.value?.addEventListener('mousedown', mouseDown);
  drawRef.value?.addEventListener('touchstart', touchStart);
  document.body.addEventListener('contextmenu', (event) => {
    event.preventDefault();
  });
});

onUnmounted(() => {
  drawRef.value?.removeEventListener('mousedown', mouseDown);
  window.removeEventListener('mouseup', mouseUp);
  window.removeEventListener('mousemove', mouseMove);

  window.removeEventListener('keydown', onKeyDown);
  window.removeEventListener('keyup', onKeyUp);
});

const style = computed(() => {
  if (!rectangle.value) {
    return {
      left: '0px',
      top: '0px',
      width: '0px',
      height: '0px',
      borderColor: color.value,
    };
  }
  return {
    left: rectangle.value.x - 1 + 'px',
    top: rectangle.value.y - 1 + 'px',
    width: rectangle.value.width + 2 + 'px',
    height: rectangle.value.height + 2 + 'px',
    color: color.value,
    position: 'absolute' as const,
    borderColor: color.value,
  };
});
</script>

<template>
  <div ref="drawRef" class="drawing-area">
    <div class="relative h-full w-full">
      <div v-if="style.width !== '0px'" class="h-full w-full rounded-4 border-[2px] border-dotted" :style="style"></div>
    </div>

    <!--    <svg width="100%" height="100%" fill="none" xmlns="http://www.w3.org/2000/svg">-->
    <!--      <rect :x="style.x" :y="style.y" :width="style.width" :height="style.height"-->
    <!--            rx="3" :stroke="style.color" stroke-width="2" stroke-dasharray="4 4"/>-->
    <!--    </svg>-->
  </div>
</template>

<style scoped lang="scss">
.drawing-area {
  width: 100%;
  height: 100%;
  position: absolute;

  svg {
    shape-rendering: optimizeSpeed;
    will-change: width;
    transform: translate3d(0, 0, 0);
  }

  &__rect {
    border-radius: 4px;
    border-style: dashed;
    border-width: 2px;
    border-color: white;
    position: absolute;
    pointer-events: none;
    transform: translate3d(0, 0, 0);
    cursor: default;
  }
}
</style>
